import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Factory as FactoryIcon, Plus, Trash2, Pencil, Search, ChevronLeft, ChevronRight, Package, FileDown } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ProductsGrid } from "@/components/products/ProductsGrid";
import { useProductTypes } from "@/lib/productTypes";
import { toast } from "@/components/ui/sonner";

const EMPTY = { name: "", contact_person: "", email: "", phone: "", buyer: "" };

/**
 * 工厂管理 — 2026-06 主从布局（用户规格）：
 *   左侧 = 纯工厂列表（点击进入编辑详细资料）
 *   右侧 = 该工厂对应产品的 Grid view（每页 25 / 50，点击产品进入与产品清单页
 *          完全相同的产品详细资料页）
 */
export default function Factories() {
  const { t, lang } = useLanguage();
  const { user } = useAuth();
  const isBuyer = user?.role === "buyer";
  const [searchParams, setSearchParams] = useSearchParams();
  const [factories, setFactories] = useState([]);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null); // null = closed, {id?} = dialog open

  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(Number(searchParams.get("page")) || 1);
  const [pageSize, setPageSize] = useState(Number(searchParams.get("size")) || 25);
  // 2026-06 · 产品类型筛选 — 默认 标准产品，"all" = 全部类型。
  const [typeFilter, setTypeFilter] = useState(searchParams.get("type") || "standard");
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [exporting, setExporting] = useState(false);
  const productTypes = useProductTypes();

  const toggleSelect = (itemNumber) => setSelectedItems((prev) => {
    const next = new Set(prev);
    next.has(itemNumber) ? next.delete(itemNumber) : next.add(itemNumber);
    return next;
  });

  // 2026-06 · 一键导出核价 Excel — 只导出勾选的产品（用户规格）。
  const exportPriceCheck = async () => {
    const item_numbers = [...selectedItems];
    if (!item_numbers.length) return;
    setExporting(true);
    try {
      const { data } = await api.post("/factories/export/price-check",
        { item_numbers, factory: selected?.name },
        { responseType: "blob", timeout: 180000 });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${selected?.name || "factory"}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("quote.exportError"));
    } finally {
      setExporting(false);
    }
  };

  const fetchFactories = useCallback(async () => {
    try {
      const { data } = await api.get("/factories");
      setFactories(data);
      // 2026-06 · 从产品编辑页返回时按 URL 参数恢复之前选中的工厂。
      const wanted = searchParams.get("factory");
      setSelected((cur) => cur || (wanted && data.find((f) => f.name === wanted)) || data[0] || null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }, []);

  useEffect(() => { fetchFactories(); }, [fetchFactories]);

  // 2026-06 · 把选中的工厂 / 页码写进 URL，并登记为产品编辑页的「返回」目标，
  // 这样从工厂的产品清单点进产品，返回时会回到同一个工厂、同一页。
  useEffect(() => {
    if (!selected?.name) return;
    const next = { factory: selected.name, page: String(page), size: String(pageSize), type: typeFilter };
    setSearchParams(next, { replace: true });
    try {
      sessionStorage.setItem("pdl_return_url", `/factories?factory=${encodeURIComponent(selected.name)}&page=${page}&size=${pageSize}&type=${encodeURIComponent(typeFilter)}`);
      sessionStorage.setItem("pdl_return_label", selected.name);
    } catch { /* storage disabled */ }
  }, [selected, page, pageSize, typeFilter, setSearchParams]);

  // 上一个 / 下一个 在产品编辑页内按这个工厂的产品顺序走。
  useEffect(() => {
    if (!products.length) return;
    try {
      sessionStorage.setItem("pdl_scope_list", JSON.stringify(products.map((p) => p.item_number)));
    } catch { /* storage disabled */ }
  }, [products]);

  useEffect(() => {
    if (!selected?.name) { setProducts([]); setTotal(0); return; }
    setSelectedItems(new Set());
    let cancelled = false;
    setLoadingProducts(true);
    // 2026-06 · 默认只显示 标准产品(standard)，可切换类型/全部；按 产品编号 升序排列。
    api.get("/products", { params: { factory: selected.name, page, page_size: pageSize, show_all: "1",
                                     ...(typeFilter !== "all" ? { product_type: typeFilter } : {}),
                                     sort: "item_number" } })
      .then(({ data }) => {
        if (cancelled) return;
        setProducts(data.items || []);
        setTotal(data.total || 0);
      })
      .catch((e) => !cancelled && toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => !cancelled && setLoadingProducts(false));
    return () => { cancelled = true; };
  }, [selected, page, pageSize, typeFilter]);

  const handleSave = async () => {
    try {
      if (editing.id) {
        await api.put(`/factories/${editing.id}`, editing);
        if (selected?.id === editing.id) setSelected({ ...editing });
      } else {
        await api.post("/factories", editing);
      }
      setEditing(null);
      fetchFactories();
      toast.success(t("common.save"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const handleDelete = async (f) => {
    try {
      await api.delete(`/factories/${f.id}`);
      if (selected?.id === f.id) setSelected(null);
      fetchFactories();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const setField = (field, value) => setEditing((f) => ({ ...f, [field]: value }));

  const visible = factories.filter((f) => {
    if (!query.trim()) return true;
    const q = query.trim().toLowerCase();
    return [f.name, f.contact_person, f.buyer, f.email, f.phone]
      .some((v) => (v || "").toLowerCase().includes(q));
  });
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="animate-fade-in-up">
      <div className="flex items-center justify-between mb-3">
        <h1 className="font-heading text-base font-bold tracking-tight flex items-center gap-2">
          <FactoryIcon size={18} /> {t("factories.title")}
          <span className="text-xs font-normal text-muted-foreground font-mono" data-testid="factories-count">
            {factories.length}
          </span>
        </h1>
        <Button onClick={() => setEditing({ ...EMPTY, buyer: isBuyer ? user.name : "" })} size="sm" data-testid="new-factory-button" className="rounded-md h-9 bg-brand hover:bg-brand-hover transition-colors">
          <Plus size={15} /> {t("quote.new")}
        </Button>
      </div>

      <div className="flex gap-3 items-start">
        {/* ── 左：工厂列表 ───────────────────────────────── */}
        <aside className="w-[260px] shrink-0 bg-card border border-border rounded-md overflow-hidden" data-testid="factories-sidebar">
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("factories.searchPlaceholder")}
                data-testid="factories-search-input"
                className="pl-8 h-9 rounded-md text-sm"
              />
            </div>
          </div>
          <div className="max-h-[calc(100vh-190px)] overflow-y-auto divide-y divide-border/60" data-testid="factories-list">
            {visible.length === 0 && (
              <p className="text-center py-10 text-sm text-muted-foreground" data-testid="factories-empty-state">
                {t("factories.noFactories")}
              </p>
            )}
            {visible.map((f) => (
              <div
                key={f.id}
                onClick={() => { setSelected(f); setPage(1); }}
                data-testid={`factory-row-${f.name}`}
                className={`group flex items-center gap-1 px-2.5 py-2 cursor-pointer transition-colors ${
                  selected?.id === f.id ? "bg-brand/10" : "hover:bg-secondary/50"
                }`}
              >
                <div className="min-w-0 flex-1">
                  <p className={`text-sm font-semibold truncate ${selected?.id === f.id ? "text-brand" : ""}`}>{f.name}</p>
                  <p className="text-[11px] text-muted-foreground truncate">
                    {[f.contact_person, f.buyer].filter(Boolean).join(" · ") || "-"}
                  </p>
                </div>
                <Button
                  variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={(e) => { e.stopPropagation(); setEditing({ ...f }); }}
                  title={t("common.edit")} data-testid={`edit-factory-${f.name}`}
                >
                  <Pencil size={13} />
                </Button>
                <Button
                  variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={(e) => { e.stopPropagation(); handleDelete(f); }}
                  title={t("common.delete")} data-testid={`delete-factory-${f.name}`}
                >
                  <Trash2 size={13} className="text-destructive" />
                </Button>
              </div>
            ))}
          </div>
        </aside>

        {/* ── 右：该工厂的产品 Grid ───────────────────────── */}
        <section className="flex-1 min-w-0" data-testid="factory-products-panel">
          {!selected ? (
            <div className="bg-card border border-border rounded-md py-20 text-center text-sm text-muted-foreground">
              {t("factories.pickFactoryHint")}
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <h2 className="text-base font-bold tracking-tight" data-testid="factory-products-title">{selected.name}</h2>
                <span className="inline-flex items-center gap-1 text-xs text-muted-foreground font-mono" data-testid="factory-products-total">
                  <Package size={13} /> {total}
                </span>
                <Button variant="outline" size="sm" className="h-9 rounded-md" onClick={() => setEditing({ ...selected })} data-testid="factory-edit-details-button">
                  <Pencil size={13} /> {t("factories.editDetails")}
                </Button>
                <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setPage(1); }}>
                  <SelectTrigger className="h-9 w-[132px] rounded-md text-xs" data-testid="factory-products-type-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all" data-testid="factory-type-option-all">{lang === "en" ? "All types" : "全部类型"}</SelectItem>
                    {productTypes.map((pt) => (
                      <SelectItem key={pt.code} value={pt.code} data-testid={`factory-type-option-${pt.code}`}>
                        {lang === "en" ? (pt.name_en || pt.code) : (pt.name_zh || pt.code)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {products.length > 0 && (
                  <>
                    <Button variant="outline" size="sm" className="h-9 rounded-md"
                      onClick={() => setSelectedItems(selectedItems.size === products.length
                        ? new Set()
                        : new Set(products.map((p) => p.item_number)))}
                      data-testid="factory-select-all-button">
                      {selectedItems.size === products.length ? (lang === "en" ? "Clear" : "取消全选") : (lang === "en" ? "Select page" : "全选本页")}
                    </Button>
                    <Button size="sm" className="h-9 rounded-md bg-brand hover:bg-brand-hover"
                      disabled={!selectedItems.size || exporting} onClick={exportPriceCheck}
                      data-testid="factory-export-excel-button">
                      <FileDown size={13} /> {exporting ? "..." : `${lang === "en" ? "Export Excel" : "导出核价表"}${selectedItems.size ? ` (${selectedItems.size})` : ""}`}
                    </Button>
                  </>
                )}
                <div className="ml-auto flex items-center gap-2">
                  <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
                    <SelectTrigger className="h-9 w-[104px] rounded-md text-xs" data-testid="factory-products-page-size">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="25">25 / {t("common.page")}</SelectItem>
                      <SelectItem value="50">50 / {t("common.page")}</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="flex items-center gap-1">
                    <Button variant="outline" size="icon" className="h-9 w-9 rounded-md" disabled={page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="factory-products-prev">
                      <ChevronLeft size={15} />
                    </Button>
                    <span className="text-xs font-mono px-1 tabular-nums" data-testid="factory-products-page">{page} / {totalPages}</span>
                    <Button variant="outline" size="icon" className="h-9 w-9 rounded-md" disabled={page >= totalPages}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))} data-testid="factory-products-next">
                      <ChevronRight size={15} />
                    </Button>
                  </div>
                </div>
              </div>
              {!loadingProducts && products.length === 0 ? (
                <div className="bg-card border border-border rounded-md py-16 text-center text-sm text-muted-foreground" data-testid="factory-products-empty">
                  {t("factories.noProducts")}
                </div>
              ) : (
                <ProductsGrid products={products} loading={loadingProducts} canSelect
                  selected={selectedItems} onToggleSelect={toggleSelect} density="compact" />
              )}
            </>
          )}
        </section>
      </div>

      <Dialog open={Boolean(editing)} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md" data-testid="factory-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-base">
              {editing?.id ? t("factories.editTitle") : t("factories.newTitle")}
            </DialogTitle>
            <DialogDescription className="sr-only">{t("factories.title")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>{t("factories.name")} *</Label>
              <Input value={editing?.name || ""} onChange={(e) => setField("name", e.target.value)} data-testid="factory-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{t("factories.contactPerson")}</Label>
                <Input value={editing?.contact_person || ""} onChange={(e) => setField("contact_person", e.target.value)} data-testid="factory-contact-input" />
              </div>
              <div className="space-y-1.5">
                <Label>{t("factories.buyer")}</Label>
                <Input value={editing?.buyer || ""} disabled={isBuyer} onChange={(e) => setField("buyer", e.target.value)} data-testid="factory-buyer-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{t("factories.email")}</Label>
                <Input type="email" value={editing?.email || ""} onChange={(e) => setField("email", e.target.value)} data-testid="factory-email-input" />
              </div>
              <div className="space-y-1.5">
                <Label>{t("factories.phone")}</Label>
                <Input value={editing?.phone || ""} onChange={(e) => setField("phone", e.target.value)} data-testid="factory-phone-input" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="rounded-md">{t("common.cancel")}</Button>
            <Button onClick={handleSave} data-testid="save-factory-button" className="rounded-md bg-brand hover:bg-brand-hover">
              {t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
